import type { Collection, Violation } from '@/types/api'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly violations: Violation[] = [],
    public readonly field: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Chyby validace namapované na názvy polí formuláře. */
  get fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {}
    for (const violation of this.violations) {
      if (!errors[violation.propertyPath]) {
        errors[violation.propertyPath] = violation.message
      }
    }
    if (this.field && !errors[this.field]) {
      errors[this.field] = this.message
    }
    return errors
  }
}

type QueryValue = string | number | boolean | undefined | null

/** Pole se pošle jako opakovaný parametr — klíč s `[]` (např. `stores[]`) čte Symfony jako pole. */
type Query = Record<string, QueryValue | QueryValue[]>

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Query
  /** Pojistka proti smyčce refresh → 401 → refresh. */
  retryOnUnauthorized?: boolean
  headers?: Record<string, string>
}

/** Volá se, když ani obnova tokenu session nezachrání. */
let onSessionExpired: (() => void) | null = null

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

let refreshPromise: Promise<boolean> | null = null

/**
 * Souběžné 401 nesmí spustit několik refreshů naráz — všechny čekají
 * na jeden probíhající pokus.
 */
async function refreshSession(): Promise<boolean> {
  refreshPromise ??= fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      queueMicrotask(() => {
        refreshPromise = null
      })
    })

  return refreshPromise
}

function buildUrl(path: string, query?: Query): string {
  if (!query) return path

  const params = new URLSearchParams()
  for (const [key, raw] of Object.entries(query)) {
    for (const value of Array.isArray(raw) ? raw : [raw]) {
      if (value === undefined || value === null || value === '') continue
      params.append(key, String(value))
    }
  }

  const queryString = params.toString()
  return queryString ? `${path}?${queryString}` : path
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: Record<string, unknown> = {}
  try {
    payload = (await response.json()) as Record<string, unknown>
  } catch {
    // Odpověď bez JSON těla (timeout proxy, HTML chybovka) — vystačíme si se statusem.
  }

  const violations = Array.isArray(payload.violations) ? (payload.violations as Violation[]) : []

  const message =
    (typeof payload.message === 'string' && payload.message) ||
    (typeof payload.detail === 'string' && payload.detail) ||
    (typeof payload.title === 'string' && payload.title) ||
    `Požadavek selhal (${response.status})`

  const field = typeof payload.field === 'string' ? payload.field : null

  return new ApiError(response.status, message, violations, field)
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, query, headers = {} } = options

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  }

  if (body !== undefined) {
    // API Platform očekává u PATCH merge-patch content type.
    requestHeaders['Content-Type'] =
      method === 'PATCH' ? 'application/merge-patch+json' : 'application/json'
  }

  return fetch(buildUrl(path, query), {
    method,
    credentials: 'same-origin',
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { retryOnUnauthorized = true } = options
  let response = await rawRequest(path, options)

  // Access token má krátkou životnost; při 401 se ho pokusíme jednou obnovit
  // a request zopakovat, aby uživatel nepřišel o rozdělanou práci.
  if (response.status === 401 && retryOnUnauthorized && !path.startsWith('/api/auth/')) {
    if (await refreshSession()) {
      response = await rawRequest(path, options)
    } else {
      onSessionExpired?.()
      throw await toApiError(response)
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

/**
 * Kolekce z API Platform ve formátu `json` přijde jako holé pole,
 * počty jsou v hlavičkách (viz PaginationHeaderProcessor na backendu).
 */
export async function requestCollection<T>(path: string, query?: Query): Promise<Collection<T>> {
  let response = await rawRequest(path, { query })

  if (response.status === 401) {
    if (await refreshSession()) {
      response = await rawRequest(path, { query })
    } else {
      onSessionExpired?.()
      throw await toApiError(response)
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  const items = (await response.json()) as T[]
  const readNumber = (header: string, fallback: number): number => {
    const value = Number(response.headers.get(header))
    return Number.isFinite(value) && value > 0 ? value : fallback
  }

  return {
    items,
    total: readNumber('X-Total-Count', items.length),
    page: readNumber('X-Current-Page', 1),
    pages: readNumber('X-Total-Pages', 1),
    itemsPerPage: readNumber('X-Items-Per-Page', items.length || 25),
  }
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  collection: requestCollection,
}
